import { TimePoint } from "@/components/shared/chart/ChartUtils";
import { MultiChartConfig } from "@/components/shared/chart/MultiChartWithSharedRange";
import { patientNamespace } from "@/store/modules/patient";
import { Patient } from "@/store/modules/patient/types";
import { userNamespace } from "@/store/modules/user";
import {
  Preference,
  PreferenceKeys,
  TemperatureTypes
} from "@/store/modules/user/types";
import {
  c2Requested,
  c2RequestedGraph
} from "@/utilities/temperatureConversion";
import Chart from "chart.js";
import _ from "lodash";
import Vue from "vue";
import Component from "vue-class-component";
import { Action, Getter } from "vuex-class";

@Component
export default class BodySharedMinData extends Vue {
  minChartHeight = "170px";
  minLegendChartHeight = "200px";
  minStackedChartHeight = "250px";

  @Getter("graph", patientNamespace)
  graph!: any;

  @Getter("current", patientNamespace)
  patient!: Patient;

  @Getter("preferenceTemperature", userNamespace)
  preferenceTemperature!: TemperatureTypes;

  @Getter("preferences", userNamespace)
  preferences!: Record<string, any>;

  @Action("setPreference", userNamespace)
  setPreference!: (preference: Preference) => void;

  updatePreferenceTemperature(value: TemperatureTypes) {
    this.setPreference({
      key: PreferenceKeys.TEMPERATURE,
      value
    });
  }
  mounted() {
    if (!this.preferences[PreferenceKeys.TEMPERATURE]) {
      // If no preference set, update to F for mindata patients
      this.updatePreferenceTemperature("F");
    }
  }

  get stackedChart() {
    return {
      scales: {
        yAxes: [
          {
            stacked: true
          }
        ]
      },
      elements: {
        point: {
          radius: 1,
          pointHoverRadius: 2
        }
      },
      tooltips: {
        mode: "index",
        position: "nearest",
        itemSort: (a: Chart.ChartTooltipItem, b: Chart.ChartTooltipItem) => {
          // Sort so High is on top and Low is on bottom
          if (a.datasetIndex !== undefined && b.datasetIndex !== undefined) {
            return b.datasetIndex - a.datasetIndex;
          }
        }
      }
    };
  }

  get activityData() {
    let previousFill: number | null = null;
    return this.graph([
      { key: "ActivityLow", label: "Low" },
      { key: "ActivityMed", label: "Medium" },
      { key: "ActivityHigh", label: "High" }
    ]).map((g: any, index: number) => {
      // Fill from first dataset with data to orgin or
      // from previous data set to current data set
      if (g.data.length) {
        if (previousFill === null) {
          g.fill = "origin";
        } else {
          g.fill = previousFill;
        }
        previousFill = index;
      }

      const group = g.data.reduce(
        (reducedGroup: Record<string, TimePoint>, nextPoint: TimePoint) => {
          const start = nextPoint.x.startOf("hour");
          // toString here converts to GMT from local which is what we want to pass to the charting library
          const key = start.toString();
          if (!reducedGroup[key]) {
            reducedGroup[key] = { x: start, y: 0 };
          }
          reducedGroup[key].y += nextPoint.y;

          return reducedGroup;
        },
        {}
      );

      g.data = Object.keys(group).map(key => {
        return group[key];
      });

      return g;
    });
  }

  get charts(): MultiChartConfig[] {
    const library = {
      scales: {
        yAxes: [
          {
            ticks: {
              maxTicksLimit: 6
            }
          }
        ]
      }
    };

    const graphConfigs = [];

    graphConfigs.push({
      data: c2RequestedGraph(
        this.graph([
          { key: "Temperature", label: "Skin" },
          { key: "BodyTempEstimated", label: "Estimated Body" }
        ]),
        this.preferenceTemperature
      ),
      title: `Temperature (°${this.preferenceTemperature})`,
      description:
        "Estimated Body Temperature is based on skin temperature and other parameters",
      library: library,
      suggestedMin: c2Requested(34, this.preferenceTemperature),
      suggestedMax: c2Requested(40, this.preferenceTemperature),
      minHeight: this.minLegendChartHeight
    });
    graphConfigs.push({
      data: this.graph([{ key: "HeartRate", label: "Heart Rate" }]),
      title: "Heart Rate",
      description:
        "Heart Rate is collected at rest and is reported in beats per minute (bpm)",
      library: library,
      suggestedMax: 150,
      minHeight: this.minChartHeight
    });
    graphConfigs.push({
      data: this.graph([{ key: "RespiratoryRate", label: "Respiratory Rate" }]),
      title: "Resp. Rate",
      description:
        "Respiratory Rate is collected at rest and is reported is respirations per minute (rpm)",
      library: library,
      suggestedMin: 0,
      suggestedMax: 30,
      minHeight: this.minChartHeight
    });

    const coughAtom = _.get(this.patient, "body.atoms.cough");

    if (coughAtom && coughAtom.age >= 0) {
      graphConfigs.push({
        data: this.graph(["Cough"]),
        title: "Severe Cough Episodes",
        description:
          "Severe Cough is a count of how many coughing episodes were detected",
        library: library,
        suggestedMin: 0,
        suggestedMax: 14,
        minHeight: this.minChartHeight
      });
    }
    graphConfigs.push({
      data: this.activityData,
      title: "Activity",
      description:
        "Activity is a measurement of your movement throughout the day",
      library: this.stackedChart,
      suggestedMin: 0,
      suggestedMax: 60,
      minHeight: this.minStackedChartHeight
    });
    return graphConfigs;
  }

  removeParts = [
    "liver",
    "liver",
    "leftKidney",
    "rightKidney",
    "uterus",
    "ulcer",
    "pump",
    "spine",
    "ett",
    "icd",
    "rightKidneyText",
    "leftKidneyText",
    "eblText",
    "heartVolumeText",
    "lungsText",
    "brainText",
    "chestTube"
  ];
}
